import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { gql } from "@apollo/client";
import { omit } from "lodash";
import { useNavigate } from "react-router-dom";

import { useReactivateProviderMutation, useUpsertUserMutation } from "~/graphql/__generated__/graphql";
import { useSnackbar } from "~/hooks/useSnackbar";
import Help from "../common/Help";
import ProviderDeactivationModal from "./ProviderDeactivationModal";
import ProviderDetailsForm from "./ProviderDetailsForm";
import {
  UserDetails,
  UserDropDownOptions,
  DEFAULT_PROVIDER_DETAILS,
} from "./userTypes";

interface Props {
  loading: boolean,
  user: {id?: number, details: UserDetails},
  options: UserDropDownOptions,
}

const UserDetailsScreen: React.FC<Props> = ({loading, user, options}) => {
  const [existingUserId, setExistingUserId] = useState<number | undefined>(user.id);
  const [originalFormData, setOriginalFormData] = useState<UserDetails>(user.details);
  const [formData, setFormData] = useState<UserDetails>(user.details);
  const [formModified, setFormModified] = useState<boolean>(false);
  const navigate = useNavigate();
  const { setErrorMessage: displayError } = useSnackbar();
  const [shouldDisplayDeactivationModal, setShouldDisplayDeactivationModal] = useState(false);

  useEffect(() => {
    setExistingUserId(user.id);
    setOriginalFormData(user.details);
    setFormData(user.details);
    setFormModified(false);
   }, [user]);

  const [errors, setErrors] = useState<Partial<Record<keyof UserDetails, string>>>({});

  const [saveChanges, { loading: saving }] = useUpsertUserMutation({
    update: (cache) => {
      cache.evict({ id: "ROOT_QUERY", fieldName: 'searchUser' }); // Clear out user search results
    }
  });

  const [reactivateUser] = useReactivateProviderMutation();

  const formDisabled = saving || (originalFormData.deleted && formData.deleted);

  const resetForm = () => {
    setFormData(originalFormData);
    setFormModified(false);
    setErrors({});
  }

  const handleChange = (
    name: string,
    value: number[] | string[] | string | number | boolean,
  ) => {
    const [userField, providerField] = name.split(".");
    const newFormData = {
      ...formData,
      [userField]: providerField ? {
        ...formData.provider,
        [providerField]: value
      } : value
    };
    setFormModified(true);
    setFormData(newFormData);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleMultiSelectChange = (
    e: SelectChangeEvent<number[]>,
  ) => {
    const { name, value: rawValue } = e.target;
    const value = typeof rawValue === "string" ? rawValue.split(",").map(Number) : rawValue;
    handleChange(name, value);
  };

  const validate = (data: UserDetails): boolean => {
    const newErrors: Partial<Record<keyof UserDetails, string>> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Invalid email format.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildSavePayload = (
    data: UserDetails,
    existingId: number | undefined
  ): UserDetails => {
    if (!data.userIsProvider && !existingId) {
      return { ...data, provider: DEFAULT_PROVIDER_DETAILS };
    }
    return data;
  };

  const handleSubmit = (e?: React.FormEvent, overrideFormData?: UserDetails) => {
    e?.preventDefault();
    const dataToSave = overrideFormData || formData;
    if (validate(dataToSave)) {
      const userDetails = buildSavePayload(dataToSave, existingUserId);
      const payload = omit(userDetails, "provider.id", "userIsProvider");

      saveChanges({
        variables: {
          currentId: existingUserId ?? null,
          currentEmail: originalFormData.email,
          userDetails: payload,
        },
        awaitRefetchQueries: true,
        refetchQueries: ["UserDetail", "CommandIndexProviders", "AvailabilityByTimespan"],
      }).then((data) => {
        const newId = data.data?.upsertUser?.id; 
        const isNewUser = existingUserId != newId;
        if (!isNewUser) {
          setFormModified(false);
        }

        const shouldReactivateProvider = 
          existingUserId &&
          originalFormData.provider.id &&
          !originalFormData.provider.active &&
          dataToSave.provider.active;
        
        if (shouldReactivateProvider) {
          reactivateUser({
            variables: {
              providerId: originalFormData.provider.id
            },
            refetchQueries: ["AvailabilityByTimespan"]
          });
        }

        setOriginalFormData(prev => ({
          ...dataToSave,
          provider: {
            ...dataToSave.provider,
            id: prev.provider.id,
          }
        }));

        if(isNewUser) {
          navigate(`/users/manage/${newId}`, { replace: true })
        }
      }).catch((error) => {
        console.error(error)
        displayError("Error saving user")
      })
    }
  };

  const isProviderFormDisabled = useMemo(() => 
    formDisabled || formData.deleted,
    [formDisabled, formData.deleted]
  );

  const shouldTriggerDeactivationModal = useMemo(() => {
    if (!existingUserId) return false;
    const providerWasDeactivated = originalFormData.provider.active && 
      !formData.provider.active && 
      formData.userIsProvider !== originalFormData.userIsProvider;
    const userWasDeactivated = !originalFormData.deleted && 
      formData.deleted && 
      !!formData.provider?.id;
    return providerWasDeactivated || userWasDeactivated;
  }, [existingUserId, originalFormData, formData]);

  return (<>
    {loading && (
      <Box sx={{display: "flex", flexDirection: "column", gap: 4}}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignSelf: "center"}}>
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
          <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", mt: 1, mb: 1}} width={450} />
        </Box>
        <Skeleton variant="rectangular" sx={{ fontSize: "1.7rem", alignSelf: "center"}} width={400} />
      </Box>
    )}
    <ProviderDeactivationModal
      open={shouldDisplayDeactivationModal}
      handleClose={() => {
        setShouldDisplayDeactivationModal(false);
        resetForm();
      }}
      onSuccess={() => {
        handleSubmit();
        setShouldDisplayDeactivationModal(false);
      }}
    />
    {(!loading && originalFormData.deleted) && <Alert severity="warning">
      This user has been deactivated. 
      <Button
          onClick={() => {
            const reactivatedFormData = { 
              ...formData, 
              deleted: false,
              provider: {
                ...formData.provider,
                active: formData.userIsProvider,
              },
            };
            setFormData(reactivatedFormData);
            handleSubmit(undefined, reactivatedFormData);
          }}
          size="small"
          sx={{padding: 0, ml: 1, mb: "3px"}}
        >
          Reactivate
      </Button>
      </Alert>}
    {(!loading && user) && (<Box
      component="form"
      onSubmit={(e) => {
        if (shouldTriggerDeactivationModal) {
          setShouldDisplayDeactivationModal(true);
          e.preventDefault();
          return;
        }
        handleSubmit(e);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        maxWidth: 900,
        margin: "0 auto",
        mt: 4,
      }}
    >
      {/* User Details Section */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <TextField
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          error={Boolean(errors.firstName)}
          helperText={errors.firstName}
          style={{ backgroundColor: "white" }}
          disabled={formDisabled}
          fullWidth
          required
        />
        <TextField
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          error={Boolean(errors.lastName)}
          helperText={errors.lastName}
          style={{ backgroundColor: "white" }}
          disabled={formDisabled}
          fullWidth
          required
        />
        <TextField
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          error={Boolean(errors.email)}
          helperText={errors.email}
          style={{ backgroundColor: "white" }}
          disabled={formDisabled}
          fullWidth
          required
        />
        <FormControl fullWidth>
          <InputLabel>{options.roles.length ? "Roles" : "Roles (not configured)"}</InputLabel>
          <Select<number[]>
            multiple
            label={options.roles.length ? "Roles" : "Roles (not configured)"}
            value={formData.roles}
            style={{ backgroundColor: "white" }}
            disabled={formDisabled || options.roles.length == 0}
            onChange={handleMultiSelectChange}
            name="roles"
            data-testid="roles"
          >
            {options.roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>
            {options.serviceLines.length ? "Service Lines" : "Service Lines (not configured)"}
          </InputLabel>
          <Select<number[]>
            multiple
            name="serviceLines"
            data-testid="serviceLines"
            label={options.serviceLines.length ? "Service Lines" : "Service Lines (not configured)"}
            value={formData.serviceLines}
            style={{ backgroundColor: "white" }}
            disabled={formDisabled || options.serviceLines.length === 0}
            onChange={handleMultiSelectChange}
          >
            {options.serviceLines.map((line) => (
              <MenuItem key={line.id} value={line.id}>
                {line.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth error={Boolean(errors.organizationalUnits)}>
          <InputLabel>
            {options.organizationalUnits.length ? "Organizational Units" : "Organizational Units (not configured)"}
          </InputLabel>
          <Select<number[]>
            multiple
            name="organizationalUnits"
            data-testid="organizationalUnits"
            label={options.organizationalUnits.length ? "Organizational Units" : "Organizational Units (not configured)"}
            value={formData.organizationalUnits}
            style={{ backgroundColor: "white" }}
            disabled={formDisabled || options.organizationalUnits.length === 0}
            onChange={handleMultiSelectChange}
          >
            {options.organizationalUnits.map((unit) => (
              <MenuItem
                key={unit.id}
                value={unit.id}
                disabled={!unit.active && !formData.organizationalUnits.includes(unit.id)}
              >
                {unit.name}{unit.active ? "" : " (retired)"}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.organizationalUnits}</FormHelperText>
        </FormControl>
        <FormControlLabel
          data-testid="active-user-checkbox"
          control={
            <Checkbox sx={{pt: 0, pb: 0}}
              name="deleted"
              checked={!formData.deleted}
              disabled={formDisabled}
              onChange={(e) => {
                const newDeleted = !e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  deleted: newDeleted,
                  provider: { 
                    ...prev.provider, 
                    active: newDeleted ? false : prev.userIsProvider 
                  }
                }));
                setFormModified(true);
              }}
            />
          }
          label="Active"
        />
        <FormControlLabel
          control={
            <Checkbox sx={{pt: 0, pb: 0}}
              name="userIsProvider"
              checked={formData.userIsProvider}
              disabled={isProviderFormDisabled}
              onChange={(e) => {
                const newUserIsProvider = e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  userIsProvider: newUserIsProvider,
                  provider: { 
                    ...prev.provider, 
                    active: newUserIsProvider 
                  }
                }));
                setFormModified(true);
              }}
            />
          }
          label={<>User is a provider<Help text="If the provider is active or not. Deactivated providers do not appear in the the command center, when adding shifts or scheduling."/></>}
        />
      </Box>

      {/* Provider Details Section */}
      {formData.userIsProvider && (
        <ProviderDetailsForm
          provider={formData.provider}
          options={{
            providerTypes: options.providerTypes,
            teams: options.teams,
            genders: options.genders,
            languages: options.languages,
            states: options.states,
          }}
          disabled={isProviderFormDisabled}
          loading={loading}
          onChange={handleChange}
          onAddressChange={(address) => {
            setFormModified(true);
            setFormData(prev => ({ ...prev, provider: { ...prev.provider, ...address } }));
          }}
        />
      )}

      <Box sx={{alignSelf: "center"}}>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={!formModified || formDisabled || (formData.provider.active && !formData.provider.addressStreet)}
          sx={{
            paddingX: 4,
            mr: 2,
            width: "fit-content",
          }}>Save</Button>
        <Button 
          variant="outlined"
          color="secondary" 
          disabled={!formModified || formDisabled}
          onClick={resetForm}
          sx={{
            paddingX: 4,
            width: "fit-content",
            background: "white",
          }}>Reset</Button>
        </Box>
    </Box>)}
  </>)
}

export default UserDetailsScreen;

gql(`
  mutation upsertUser(
    $currentId: Int
    $currentEmail: String
    $userDetails: UpsertUserDetails!
  )
  @permission(requires: manageUsers)
  {
    upsertUser(
      currentId: $currentId
      currentEmail: $currentEmail
      userDetails: $userDetails
    ) {
      id
    }
  }
`);

gql`
  mutation ReactivateProvider(
    $providerId: Int!,
  )
  @permission(requires: manageUsers)
  {
    reactivateProvider(
      providerId: $providerId,
    ) {
      id
    }
  }
`;
